import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  CreateUserFeedbackDto,
  CreateUserFeedbackResponseDto,
} from "@workspace/dto/feedback/create-user-feedback";
import { Button } from "@workspace/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui/components/form";
import { Label } from "@workspace/ui/components/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@workspace/ui/components/radio-group";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@workspace/ui/components/responsive-dialog";
import { Textarea } from "@workspace/ui/components/textarea";
import type { AxiosResponse } from "axios";
import Link from "next/link";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Loading } from "@/components/loading";
import { axios } from "@/lib/axios";
import { toastMutationNetworkError } from "@/lib/query-failure";
import { queryKeys } from "@/lib/query-keys";
import {
  defaultSubmitFeedbackValues,
  SubmitFeedbackSchema,
  type SubmitFeedbackValues,
} from "@/lib/schemas/submit-feedback";

type Props = {
  onOpenChange: (isOpen: boolean) => void;
  isOpen: boolean;
};

export function SubmitFeedbackModal({ isOpen, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const router = useRouter();

  const form = useForm<SubmitFeedbackValues>({
    mode: "onSubmit",
    resolver: zodResolver(SubmitFeedbackSchema),
    defaultValues: defaultSubmitFeedbackValues,
  });

  const createUserFeedbackMutation = useMutation({
    mutationFn: async (data: CreateUserFeedbackDto) => {
      const response = await axios.post<
        CreateUserFeedbackResponseDto,
        AxiosResponse<CreateUserFeedbackResponseDto>,
        CreateUserFeedbackDto
      >("/contact/create-user-feedback", data);
      return response.data;
    },
    onSuccess: () => {
      onOpenChange(false);
      toast.success("Feedback successfully captured", {
        description: "Thank you for your feedback",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.account.user });
    },
    onError: (error) => {
      if (toastMutationNetworkError(error, "Couldn't submit feedback")) {
        return;
      }
      toast.error("Uh oh! Something went wrong.", {
        description: "Failed to submit feedback",
      });
    },
  });

  const onSubmit = (values: SubmitFeedbackValues) => {
    createUserFeedbackMutation.mutate({
      ...values,
      pathname: router.pathname,
    });
  };

  const _onOpenChange = (open: boolean) => {
    onOpenChange(open);
    form.reset();
  };

  return (
    <ResponsiveDialog onOpenChange={_onOpenChange} open={isOpen}>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Give us your feedback</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            We are always working to improve user experience and your feedback
            goes a long way.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col space-y-2"
            id="modals/misc/submit-feedback"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormField
              control={form.control}
              name="score"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Score</FormLabel>
                  <FormControl>
                    <RatingSelector
                      onValueChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="feedback"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Feedback</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Love the UI!" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <ResponsiveDialogFooter>
          <Button
            disabled={createUserFeedbackMutation.isPending}
            form="modals/misc/submit-feedback"
            type="submit"
          >
            {createUserFeedbackMutation.isPending ? (
              <Loading />
            ) : (
              <span>Submit</span>
            )}
          </Button>
        </ResponsiveDialogFooter>
        <p className="text-xs">
          Have a technical issue? Contact{" "}
          <Link
            href="mailto:admin@vacwork.app"
            rel="noreferrer"
            target="_blank"
          >
            <button
              className="inline-flex items-center text-navy-glow"
              type="button"
            >
              VacWork support
            </button>
          </Link>
          .
        </p>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

type RatingSelectorProps = {
  value: number;
  onValueChange: (value: number) => void;
};

function RatingSelector({ value, onValueChange }: RatingSelectorProps) {
  return (
    <RadioGroup
      className="grid grid-cols-5 items-center transition-all duration-300 ease-in-out"
      defaultValue="5"
      value={value.toString()}
    >
      {Array.from({ length: 10 }, (_, i) => i + 1).map((i) => (
        <Label htmlFor={`${i}`} key={`score-${i}`}>
          <RadioGroupItem className="sr-only" id={`${i}`} value={`${i}`} />
          <Button
            className="w-full"
            onClick={() => onValueChange(i)}
            type="button"
            variant={i === value ? "default" : "outline"}
          >
            {i}
          </Button>
        </Label>
      ))}
    </RadioGroup>
  );
}
